/**
 * ASCII Art Generator - 闪烁效果计算
 *
 * 随机或规律性闪烁效果
 */

import type { BlinkConfig } from '../animation/types'

/**
 * 计算闪烁效果
 *
 * @param charX 字符X位置
 * @param charY 字符Y位置
 * @param time 当前时间
 * @param config 闪烁配置
 * @returns 闪烁结果
 */
export function calculateBlinkEffect(
  charX: number,
  charY: number,
  time: number,
  config: BlinkConfig
): { brightness: number; visible: boolean } {
  const { probability, speed, minBrightness, maxBrightness } = config

  // 使用位置和时间生成伪随机数
  const seed = charX * 12.9898 + charY * 78.233 + time * speed * 43.758
  const random = (Math.sin(seed) * 43758.5453) % 1

  // 判断是否闪烁（基于概率）
  const blinkPhase = Math.sin(time * speed + charX * 0.1 + charY * 0.1)

  // 亮度在 min 和 max 之间波动
  const t = (blinkPhase + 1) / 2 // 归一化到 0-1
  const brightness = minBrightness + (maxBrightness - minBrightness) * t

  // 根据概率决定可见性
  const visible = Math.abs(random) > (1 - probability)

  return {
    brightness: visible ? brightness : minBrightness * 0.3,
    visible
  }
}

/**
 * 闪烁效果类
 */
export class BlinkEffect {
  private config: BlinkConfig
  private time = 0

  constructor(config: Partial<BlinkConfig> = {}) {
    this.config = {
      probability: 0.7,
      speed: 2,
      minBrightness: 0.2,
      maxBrightness: 1,
      ...config
    }
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 计算指定位置的闪烁效果
   */
  calculate(charX: number, charY: number): { brightness: number; visible: boolean } {
    return calculateBlinkEffect(charX, charY, this.time, this.config)
  }

  /**
   * 更新配置
   */
  setConfig(config: Partial<BlinkConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * 获取当前配置
   */
  getConfig(): BlinkConfig {
    return { ...this.config }
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

/**
 * 从设置创建闪烁配置
 */
export function createBlinkConfigFromSettings(
  probability: number = 0.7,
  speed: number = 2,
  intensity: number = 0.5
): BlinkConfig {
  return {
    probability,
    speed,
    minBrightness: 1 - intensity,
    maxBrightness: 1
  }
}